// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Fiscal/DetalheNfse.tsx
//  NFS-e — visualização detalhada com ações de transmitir / cancelar
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, XCircle, RefreshCw, FileText, Building2, MapPin,
  Receipt, CreditCard, AlertCircle, CheckCircle2, Clock,
} from 'lucide-react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useNfseDetalhe } from '@/features/fiscal/hooks/useNfse';
import { nfseService } from '@/features/fiscal/services/nfseService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtPct = (v: number) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const DetalheNfse = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const id = Number(idParam ?? 0);
  const { data: nfse, isLoading, refetch, isFetching } = useNfseDetalhe(id);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [motivo, setMotivo] = useState('');

  const transmitirMut = useMutation({
    mutationFn: () => nfseService.transmitir(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['nfse'] });
      toast.success(res.motivo ?? 'Transmissão enviada');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelarMut = useMutation({
    mutationFn: () => nfseService.cancelar(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nfse'] });
      toast.success('NFS-e cancelada');
      setCancelOpen(false);
      setMotivo('');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!nfse) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/fiscal/nfse')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            NFS-e não encontrada.
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCancelada  = nfse.isCancelada === 1;
  const isAutorizada = !!nfse.sChaveNota;
  const isAguardando = nfse.statusErro === 'AGUARDANDO_TRANSMISSAO';
  const isPendente   = !isCancelada && !isAutorizada && !isAguardando;

  const podeTransmitir = !isCancelada && !isAutorizada;
  const podeCancelar   = isAutorizada && !isCancelada;

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/fiscal/nfse')}
            className="-ml-2 h-7"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Notas de Serviço
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-primary" />
            NFS-e RPS #{nfse.rps}
            {nfse.numeroNota > 0 && (
              <span className="text-muted-foreground font-normal">
                · Nº {nfse.numeroNota}
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            Emissão {fmtDate(nfse.dtEmissao)} ·{' '}
            {nfse.isAmbienteProducao ? (
              <Badge className="bg-emerald-500/10 text-emerald-600">Produção</Badge>
            ) : (
              <Badge className="bg-amber-500/10 text-amber-600">Homologação</Badge>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isCancelada && (
            <Badge className="bg-rose-500/10 text-rose-600 gap-1">
              <XCircle className="h-3 w-3" /> Cancelada
            </Badge>
          )}
          {isAutorizada && !isCancelada && (
            <Badge className="bg-emerald-500/10 text-emerald-600 gap-1">
              <CheckCircle2 className="h-3 w-3" /> Autorizada
            </Badge>
          )}
          {isAguardando && (
            <Badge className="bg-blue-500/10 text-blue-600 gap-1">
              <Clock className="h-3 w-3" /> Aguardando transmissão
            </Badge>
          )}
          {isPendente && (
            <Badge className="bg-amber-500/10 text-amber-600 gap-1">
              <AlertCircle className="h-3 w-3" /> Pendente
            </Badge>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          {podeTransmitir && (
            <Button
              size="sm"
              onClick={() => transmitirMut.mutate()}
              disabled={transmitirMut.isPending}
            >
              <Send className="mr-2 h-4 w-4" />
              {transmitirMut.isPending ? 'Transmitindo…' : 'Transmitir'}
            </Button>
          )}
          {podeCancelar && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setCancelOpen(true)}
            >
              <XCircle className="mr-2 h-4 w-4" /> Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* ── Status / chave / protocolo ─────────────────────────────────── */}
      {(nfse.sChaveNota || nfse.sProtocolo || nfse.sCodigoVerificacao || nfse.statusErro) && (
        <Card>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4 py-4 text-sm">
            <Item label="Chave da nota"          value={nfse.sChaveNota ?? '—'} mono />
            <Item label="Protocolo"               value={nfse.sProtocolo ?? '—'} mono />
            <Item label="Código de verificação"   value={nfse.sCodigoVerificacao ?? '—'} mono />
            <Item label="Status"                  value={nfse.statusErro ?? '—'} />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── Cabeçalho ────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Cabeçalho
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <Item label="RPS"           value={String(nfse.rps)} />
            <Item label="Série"         value={nfse.serie ?? '—'} />
            <Item label="Número da nota" value={nfse.numeroNota || '—'} />
            <Item label="Emissão"       value={fmtDate(nfse.dtEmissao)} />
            <Item label="Saída"         value={fmtDate(nfse.dtSaida)} />
            <Item label="Natureza"      value={nfse.naturezaOperacao ?? `#${nfse.naturezaOperacaoId}`} />
            <Item label="Item LC 116"   value={nfse.itemListaServico ?? '—'} />
            <Item label="Cód. tributação municipal" value={nfse.codigoTributacaoMunicipal ?? '—'} />
            <Item label="Pedido"        value={nfse.pedido ?? '—'} />
            <Item label="Incentivador cultural" value={nfse.incentivadorCultural ? 'Sim' : 'Não'} />
            <Item label="Optante Simples" value={nfse.optanteSimplesNacional ? 'Sim' : 'Não'} />
            <Item label="Cidade IBGE"   value={String(nfse.cidadeIbge)} />
          </CardContent>
        </Card>

        {/* ── Tomador ──────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Tomador
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <Item label="Nome / Razão"        value={nfse.nomeRazao ?? '—'} colSpan={2} />
            <Item label="CNPJ / CPF"          value={nfse.cnpjCpf ?? '—'} mono />
            <Item label="Inscrição municipal" value={nfse.inscricaoMunicipal ?? '—'} mono />
            <Item label="Inscrição estadual"  value={nfse.inscricaoEstadual ?? '—'} mono />
            <Item label="E-mail"              value={nfse.email ?? '—'} />
            <Item label="Telefone"            value={nfse.telefone ?? '—'} />
            <div className="col-span-2 mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" /> Endereço
            </div>
            <Item
              label="Logradouro / nº"
              value={`${nfse.endereco ?? '—'}${nfse.numero ? `, ${nfse.numero}` : ''}`}
              colSpan={2}
            />
            <Item label="Complemento"         value={nfse.complemento ?? '—'} />
            <Item label="Bairro"              value={nfse.bairro ?? '—'} />
            <Item label="Cidade / UF"         value={`${nfse.cidade ?? '—'}/${nfse.uf ?? '—'}`} />
            <Item label="CEP"                 value={nfse.cep ?? '—'} mono />
          </CardContent>
        </Card>
      </div>

      {/* ── Itens ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Itens de serviço{' '}
            <span className="text-muted-foreground font-normal">
              ({nfse.itens.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {nfse.descricaoServico && (
            <p className="text-sm text-muted-foreground mb-3 whitespace-pre-wrap">
              <strong className="text-foreground">Descrição: </strong>
              {nfse.descricaoServico}
            </p>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="w-20 text-center">Trib.?</TableHead>
                <TableHead className="w-20 text-right">Qtd</TableHead>
                <TableHead className="w-28 text-right">Vlr unit.</TableHead>
                <TableHead className="w-20 text-right">% ISS</TableHead>
                <TableHead className="w-28 text-right">Vlr ISS</TableHead>
                <TableHead className="w-28 text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nfse.itens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                    Nenhum item.
                  </TableCell>
                </TableRow>
              ) : (
                nfse.itens.map((item) => (
                  <TableRow key={item.item}>
                    <TableCell className="font-mono">{item.item}</TableCell>
                    <TableCell>
                      {item.descricaoServico ?? '—'}
                      {item.codigoLC && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          [{item.codigoLC}]
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.isTributavel ? '✓' : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">{item.quantidade}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(item.valorUnitario)}</TableCell>
                    <TableCell className="text-right">{fmtPct(item.aliquotaIss)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(item.valorIss)}</TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {fmt(item.valorTotalLiquido)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── Totalizadores ────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Totalizadores</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Linha label="Valor dos serviços"               valor={nfse.valorServico} />
            <Linha label="Deduções"                         valor={nfse.valorDeducoes} negativo />
            <Linha label="Desconto incondicionado"          valor={nfse.valorDescontoIncondicionado} negativo />
            <Linha label="Desconto condicionado"            valor={nfse.valorDescontoCondicionado} negativo muted />
            <Separator className="my-2" />
            <Linha label={`ISS (${fmtPct(nfse.aliquotaIss)})`} valor={nfse.valorIss} />
            <Linha label="ISS retido"                       valor={nfse.valorIssRetido} negativo />
            <RetencoesRow nfse={nfse} />
            <Separator className="my-2" />
            <Linha
              label="Valor líquido da nota"
              valor={nfse.valorNota}
              destaque
            />
          </CardContent>
        </Card>

        {/* ── Pagamentos ───────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Pagamentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {nfse.formasPagamento.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Sem formas de pagamento informadas.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Forma</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nfse.formasPagamento.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>{p.formaPagRec ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(p.valor)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-medium">Total recebido</TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {fmt(nfse.formasPagamento.reduce((a, p) => a + p.valor, 0))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Diálogo de cancelamento ────────────────────────────────────── */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <XCircle className="h-5 w-5" /> Cancelar NFS-e
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              O cancelamento é definitivo e será transmitido à prefeitura.
              Informe o motivo (mínimo 15 caracteres).
            </p>
            <div>
              <Label>Motivo</Label>
              <Textarea
                rows={3}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Descreva o motivo do cancelamento…"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {motivo.trim().length}/15 caracteres
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Voltar
            </Button>
            <Button
              variant="destructive"
              onClick={() => cancelarMut.mutate()}
              disabled={motivo.trim().length < 15 || cancelarMut.isPending}
            >
              {cancelarMut.isPending ? 'Cancelando…' : 'Confirmar cancelamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Auxiliares ──────────────────────────────────────────────────────────────

interface ItemProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  colSpan?: 1 | 2;
}
const Item = ({ label, value, mono, colSpan }: ItemProps) => (
  <div className={colSpan === 2 ? 'col-span-2' : undefined}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-sm break-all ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
  </div>
);

interface LinhaProps {
  label: string;
  valor: number;
  negativo?: boolean;
  muted?: boolean;
  destaque?: boolean;
}
const Linha = ({ label, valor, negativo, muted, destaque }: LinhaProps) => (
  <div
    className={[
      'flex justify-between',
      muted    ? 'text-muted-foreground'    : '',
      destaque ? 'text-base font-semibold pt-1' : '',
    ].join(' ')}
  >
    <span>{label}</span>
    <span className="font-mono">
      {negativo && valor > 0 ? '-' : ''}{fmt(Math.abs(valor))}
    </span>
  </div>
);

interface RetencoesRowProps {
  nfse: {
    perRetPis: number;    valorRetPis: number;
    perRetCofins: number; valorRetCofins: number;
    perRetInss: number;   valorRetInss: number;
    perRetIr: number;     valorRetIr: number;
    perRetCsll: number;   valorRetCsll: number;
  };
}
const RetencoesRow = ({ nfse }: RetencoesRowProps) => {
  const linhas = [
    { label: 'PIS',    pct: nfse.perRetPis,    val: nfse.valorRetPis    },
    { label: 'COFINS', pct: nfse.perRetCofins, val: nfse.valorRetCofins },
    { label: 'INSS',   pct: nfse.perRetInss,   val: nfse.valorRetInss   },
    { label: 'IR',     pct: nfse.perRetIr,     val: nfse.valorRetIr     },
    { label: 'CSLL',   pct: nfse.perRetCsll,   val: nfse.valorRetCsll   },
  ].filter((r) => r.pct > 0 || r.val > 0);

  if (linhas.length === 0) return null;

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground mt-1">Retenções</p>
      {linhas.map((r) => (
        <Linha
          key={r.label}
          label={`${r.label} (${fmtPct(r.pct)})`}
          valor={r.val}
          negativo
          muted
        />
      ))}
    </div>
  );
};

export default DetalheNfse;
